<script>
import AiDectectorForm from "@/components/ai_detector/ai_detector_form.vue";
import { freeToolAiDetection } from "@/api/requests/freeTools";
import formatEssay from "../utils/formatEssay";

const WRITTEN_BY_HUMAN = "HUMAN_ONLY";
const WRITTEN_BY_MIXED = "MIXED";
const WRITTEN_BY_AI = "AI_ONLY";

export default {
  name: "AiDetectorView",
  components: { AiDectectorForm },

  props: {
    requestType: {
      type: String,
      default: "normal-view", // or free-tools
    },
  },

  data() {
    return {
      loading: false,
      error: "",
      stats: null,
      essay: null,
    };
  },

  computed: {
    probabilityPrecentage() {
      return Math.round(this.stats.completely_generated_prob * 100);
    },
    totalSentences() {
      return this.stats?.sentences.length || 0;
    },
    totalSentencesAi() {
      return this.sentencesWrittenByAi.length;
    },
    sentencesWrittenByAi() {
      const sentencesArr = this.stats?.sentences || [];

      const filtered = sentencesArr.filter((sentence) => {
        if (sentence.highlight_sentence_for_ai === true) {
          return sentence;
        }
      });

      return filtered;
    },
    outcome() {
      return this.stats?.result_message || "";
    },
    iconHandler() {
      let icon;

      switch (this.stats?.document_classification) {
        case WRITTEN_BY_HUMAN:
          icon = "person-check";
          break;
        case WRITTEN_BY_AI:
          icon = "person-x";
          break;
        case WRITTEN_BY_MIXED:
          icon = "person-exclamation";
          break;
        default:
          break;
      }

      return icon;
    },
  },

  created() {
    const essayContents = this.$store.state.aiDetectorEssayContent;

    if (essayContents) {
      this.detect({ essayContents });
    }
  },
  methods: {
    async detect(payload) {
      try {
        this.loading = true;

        const { data } = await freeToolAiDetection({
          content: payload.essayContents,
        });

        this.stats = data;
        this.essay = formatEssay(data.sentences, data.paragraphs);

        this.error = "";
      } catch (error) {
        this.error = error.response?.data?.error;
      } finally {
        this.loading = false;
      }
    },

    print() {
      window.print();
    },
  },
};
</script>

<template>
  <eg-b-container>
    <div
      v-if="requestType === 'normal-view' && !stats && !loading"
      class="text-right svg-container"
    >
      <img src="../assets/illustrations/detector-illustration.svg" alt="" />
    </div>
    <section>
      <eg-b-row v-if="loading">
        <eg-b-col md="3"></eg-b-col>
        <eg-b-col md="6" class="text-center">
          <eg-b-spinner
            class="mb-4"
            style="width: 3rem; height: 3rem"
          ></eg-b-spinner>
          <h4>{{ $t("essays.evaluatingEssay") }}</h4>
        </eg-b-col>
        <eg-b-col md="3"></eg-b-col>
      </eg-b-row>
      <eg-b-row v-else>
        <eg-b-col cols="12">
          <template v-if="!stats">
            <h1 class="mb-4">{{ $t("essays.aiDetectorTitle") }}</h1>
          </template>
        </eg-b-col>

        <eg-b-col>
          <eg-b-alert v-if="error" variant="danger" show>{{
            error
          }}</eg-b-alert>
          <div v-if="!stats">
            <p class="mb-2">
              {{ $t("essays.aiDetector.uploadToDetect") }}
            </p>

            <ai-dectector-form @essaySubmitted="detect" />
          </div>

          <div v-else>
            <div class="d-flex justify-content-between mb-3">
              <h1>{{ $t("essays.aiDetector.detectionResults") }}</h1>
              <eg-b-button class="no-print" @click="print">{{
                $t("essays.aiDetector.printResults")
              }}</eg-b-button>
            </div>

            <eg-b-card class="mb-4">
              <template #header></template>
              <h5 class="mb-3">
                <eg-b-icon
                  :icon="iconHandler"
                  class="icon-purple mr-2"
                ></eg-b-icon>
                <span
                  >{{ $t("essays.aiDetector.outcome") }}: {{ outcome }}</span
                >
              </h5>
              <div class="light-grey-text">
                <p class="mb-0 font-rubik">
                  {{ $t("essays.detectionSummary") }}
                </p>
                <p class="mb-0">
                  {{ $t("essays.thereIsA") }}
                  <span class="text-primary">{{ probabilityPrecentage }}%</span>
                  {{ $t("essays.likelihoodWrittenWithAI") }}
                </p>
                <p class="mb-0 light-grey-text">
                  <span class="text-primary">
                    {{ `${totalSentencesAi}/${totalSentences}` }}
                  </span>
                  {{ $t("essays.aiDetector.sentencesShowSigns") }}
                </p>
              </div>
            </eg-b-card>

            <div>
              <h6>{{ $t("essays.aiDetector.statistics") }}</h6>

              <eg-b-progress
                :value="stats.overall_burstiness"
                variant="info"
                class="mb-3"
                max="2000"
              ></eg-b-progress>
              <p>
                {{ $t("essays.aiDetector.burstinessDescription") }}
              </p>
              <hr />
              <h3>{{ $t("essays.aiDetector.detectionBreakdown") }}</h3>

              <div class="mb-3">
                <p class="mb-3">
                  {{ $t("essays.aiDetector.sentencesLikelyAi") }}
                </p>
                <ol v-if="totalSentencesAi > 0">
                  <li
                    v-for="(obj, index) in sentencesWrittenByAi"
                    :key="index"
                    class="mb-2"
                  >
                    <span class="highlight">"{{ obj.sentence }}"</span>
                  </li>
                </ol>
                <span v-else class="d-block">{{
                  $t("essays.aiDetector.noneDetected")
                }}</span>
              </div>
              <div class="mb-5">
                <eg-b-button v-b-toggle.collapse-1 block class="text-left">
                  {{ $t("essays.aiDetector.toggleEssayPrefix") }}
                  <span class="highlight">{{
                    $t("essays.aiDetector.highlighted")
                  }}</span
                  >{{ $t("essays.aiDetector.toggleEssaySuffix") }}
                </eg-b-button>
                <eg-b-collapse id="collapse-1" class="mt-2">
                  <div class="ai-detection-essay" v-html="essay"></div>
                </eg-b-collapse>
              </div>
            </div>
          </div>
        </eg-b-col>
        <eg-b-col v-if="!stats" cols="1"></eg-b-col>
        <eg-b-col v-if="!stats" cols="6">
          <div class="help-box">
            <p class="font-rubik">
              {{ $t("essays.aiDetector.howDoesItWork") }}
            </p>
            <ul>
              <li>
                {{ $t("essays.aiDetector.howItem1") }}
              </li>
              <li>{{ $t("essays.aiDetector.howItem2") }}</li>
              <li>{{ $t("essays.aiDetector.howItem3") }}</li>
            </ul>
          </div>
        </eg-b-col>
      </eg-b-row>
    </section>
  </eg-b-container>
</template>
